/****************************************************
 * Module Name:  planning.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import { Box, Button, Tab, Tabs, Typography } from "@mui/material";
import React, { useState } from "react";
import Constants from "../../../utility/constants";
import Utility from "../../../utility/utility";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import NoPermission from "../../common/noPermission";
import SwipeableViews from "react-swipeable-views";
import PricePlanning from "./pricePlanning";
import LockedPlanning from "./lockedPlanning";
import { TbCalendarCancel, TbCalendarDollar } from "react-icons/tb";
import AddPricePlanModal from "./addPricePlanModal";
import { IoIosAddCircleOutline } from "react-icons/io";
import PricePlanningProxy from "../../../utility/proxy/manager/manufacturingProxy/pricePlanningProxy";
import CommonSnackbar from "../../common/commonSnackBar";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 1 }}>
                    <Typography component={"span"}>{children}</Typography>
                </Box>
            )}
        </Box>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `full-width-tab-${index}`,
        "aria-controls": `full-width-tabpanel-${index}`,
        value: index,
    };
}

const Planning = () => {

    const pricePlanningPermission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_PRICEPLANNING ||
        iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_PRICEPLANNING_MARKER);

    const lockedPlanningPermission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_LOCKEDPLANNING ||
        iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_LOCKEDPLANNING_MARKER);

    const theme = useTheme();
    const [value, setValue] = React.useState(Utility.isReadAllowed(pricePlanningPermission) ? 0 : (Utility.isReadAllowed(lockedPlanningPermission) ? 1 : 2));

    const [showAddPricePlanModal, setShowAddPricePlanModal] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [loader, setLoader] = useState(false);
    const [triggerSearch, setTriggerSearch] = useState(false);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleChangeIndex = (index) => {
        setValue(index);
    };

    const handleSave = (data) => {
        setLoader(true);
        PricePlanningProxy.AddPricePlanning(data, (response) => {
            if (response) {
                setShowSnackbar(true);
                setSnackbarMessage("Pricing added successfully");
                setSnackbarVariant("success");
                setLoader(false);
                setShowAddPricePlanModal(false);
                setTriggerSearch(true);
            } else {
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
                setLoader(false);
                setShowAddPricePlanModal(false);
            }
        });
    }

    return (
        value === 2 ? <NoPermission /> :
            <Box sx={{
                bgcolor: "background.paper",
                width: "100%",
                padding: "10px",
                "& .MuiButtonBase-root": {
                    minHeight: "40px",
                },
                position: "relative"
            }}>
                {showSnackbar ? (
                    <CommonSnackbar
                        show={true}
                        message={snackbarMessage}
                        variant={snackbarVariant}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={snackbarVariant === "success" ? "Success" :
                            snackbarVariant === "error" ? "Failed" : "Warning"}
                        handleClose={() => setShowSnackbar(false)}
                    />
                ) : null}
                <Tabs
                    value={value}
                    onChange={handleChange}
                >

                    {/* Price Planning */}
                    {Utility.isReadAllowed(pricePlanningPermission) ? (
                        <Tab
                            icon={<TbCalendarDollar size={18} />}
                            iconPosition="start"
                            label="Planning"
                            {...a11yProps(0)}
                        />
                    ) : null}

                    {/* Locked Planning */}
                    {Utility.isReadAllowed(lockedPlanningPermission) ? (
                        <Tab
                            icon={<TbCalendarCancel size={18} />}
                            iconPosition="start"
                            label="Locked Planning"
                            {...a11yProps(1)}
                        />
                    ) : null}

                </Tabs>
                <SwipeableViews
                    axis={theme.direction === "rtl" ? "x-reverse" : "x"}
                    index={value}
                    onChangeIndex={handleChangeIndex}
                >
                    {/* Price Planning */}
                    <TabPanel value={value} index={0}>
                        <PricePlanning triggerSearch={triggerSearch} setTriggerSearch={setTriggerSearch} />
                    </TabPanel>

                    {/* Locked Planning */}
                    <TabPanel value={value} index={1}>
                        <LockedPlanning />
                    </TabPanel>

                </SwipeableViews>

                {Utility.isReadAllowed(pricePlanningPermission) ? (
                    <Box
                        style={{
                            position: "absolute",
                            top: "14px",
                            right: "12px",
                            display: "flex",
                            alignItems: "center"
                        }}
                    >
                        <Button
                            component="label"
                            variant="contained"
                            size="small"
                            startIcon={<IoIosAddCircleOutline size={20} />}
                            onClick={() => {
                                setShowAddPricePlanModal(true);
                                setTriggerSearch(false);
                            }}
                        >
                            ADD PRICE PLAN
                        </Button>
                    </Box>
                ) : null}

                {showAddPricePlanModal ? (
                    <AddPricePlanModal
                        show={showAddPricePlanModal}
                        loader={loader}
                        handleSave={handleSave}
                        handleClose={() => setShowAddPricePlanModal(false)}
                        fromTender={false}
                    />
                ) : null}

            </Box>
    );
};

export default Planning;
